import { useTranslation } from 'react-i18next';
import { useSavedLocationApi } from '@hero/api-hooks';
import { LocationIcon } from '@hero/web-icons';
import type { Id } from '@hero/schema';
import { RegionName } from '../../../../app/RegionName/RegionName';
import { EntityLink } from './EntityLink';

export interface SavedLocationCardWidgetProps {
  savedLocationId: Id;
  displaySnapshot?: { name?: string };
}

export const SavedLocationCardWidget = ({
  savedLocationId,
  displaySnapshot,
}: SavedLocationCardWidgetProps) => {
  const { t } = useTranslation('m-agent');
  const { data: location } = useSavedLocationApi(savedLocationId);
  const name =
    location?.name ??
    displaySnapshot?.name ??
    t('widgets.saved_location_card.fallback', 'Saved location');

  const city = location?.address?.city;
  const address = location?.address?.fullAddress?.trim();
  const owner =
    location?.client?.name?.trim() ||
    t('widgets.saved_location_card.my_location', { defaultValue: 'My location' });

  return (
    <EntityLink
      kind='saved_location'
      entityId={String(savedLocationId)}
      label={name}
      // Owner first — "who is this place for" is what makes the card
      // scannable in a transcript; the address trails as context.
      subtitle={
        <>
          <span className='font-medium text-slate-600'>{owner}</span>
          {address ? <span className='text-slate-400'> · {address}</span> : null}
        </>
      }
      trailing={
        city ? (
          <span className='rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600'>
            <RegionName customCode={city.customCode} fallback={city.name} />
          </span>
        ) : null
      }
      icon={<LocationIcon className='size-4' />}
    />
  );
};
